import type { StateData } from '@/lib/become-an-agent-types';

/**
 * HostedPath
 *
 * Sits directly under the Hero on /become-an-agent/{state}. Reframes the
 * licensing/credentialing wall the reader has just absorbed: that wall belongs
 * to the independent path (building your own agency), not to becoming a travel
 * advisor. Champagne surface gives the eye a breath between deep-teal Hero and
 * the dark-teal StateFactsPanel below.
 *
 * Asymmetric two-column on md+ (8/4): body text left, oversized HAR stats
 * right. Editorial typography throughout — Didot numerals, italic Didot
 * captions, hairline separator. No card chrome, no border rule.
 */
export function HostedPath({ state }: { state: StateData }) {
    return (
        <section id="the-path" className="scroll-mt-20 bg-[#fdfaf3]">
            <div className="mx-auto max-w-6xl px-6 py-20 text-[#0e2727] sm:py-24 lg:py-28">
                <div className="grid gap-14 md:grid-cols-12 md:gap-16">
                    {/* Body column — 8 of 12 on md+ */}
                    <div className="md:col-span-8">
                        <p className="lux-caps lux-caps-sm text-[#0e2727]/55">
                            The path
                        </p>
                        <h2 className="font-didot mt-3 text-3xl text-[#0e2727] sm:text-4xl">
                            How to become a travel agent in {state.state}, in five steps.
                        </h2>
                        <ol className="mt-8 space-y-7 [&_strong]:font-semibold [&_strong]:text-[#0b2222]">
                            <Step
                                number="01"
                                title="Be honest about your network."
                            >
                                The work runs on the clients you can reach. We&rsquo;re
                                client-focused, not niche-focused &mdash; a single client might want
                                an NCL cruise, a Doubletree stay, and an Aman suite in the same
                                month. Look hard at your social circles, what they travel for, and
                                what they spend. If your network isn&rsquo;t there yet, you&rsquo;ll
                                need the time, money, and willpower to build one.{' '}
                                <strong>The starting question is simple: do I have an angle that
                                makes sense?</strong> If the answer isn&rsquo;t yes, this likely
                                isn&rsquo;t the right move.
                            </Step>
                            <Step
                                number="02"
                                title="Choose a host agency."
                            >
                                A host agency holds the Seller of Travel licenses, IATA, supplier
                                programs, and back-office systems an independent agent would otherwise
                                spend years assembling. <strong>LuxRally is one option</strong> &mdash;
                                free training, no membership fees, Forbes-endorsed, in-house concierge
                                team, direct mentorship, and proprietary booking technology built
                                in-house.
                            </Step>
                            <Step
                                number="03"
                                title="Get trained."
                            >
                                Most agencies charge for training; LuxRally&rsquo;s four-part curriculum
                                (Industry Course &rarr; Practical &rarr; Systems &rarr; Orientation) is{' '}
                                <strong>100% free for those who make it into our program</strong>.{' '}
                                <a
                                    href="/become-an-agent"
                                    className="lux-caps lux-caps-sm ml-1 inline-flex items-center gap-1 text-[#0b2222] underline-offset-4 hover:underline"
                                >
                                    Apply now <span aria-hidden="true">&rarr;</span>
                                </a>
                            </Step>
                            <Step
                                number="04"
                                title="Start booking."
                            >
                                Plug into the host&rsquo;s tools, build your client list, and learn the
                                workflow on real bookings. The first client is usually someone you
                                already know.
                            </Step>
                            <Step
                                number="05"
                                title="Scale your income."
                            >
                                Build toward luxury, secure regular group contracts, and delegate the
                                time-sucks &mdash; restaurant reservations, client profile setup
                                &mdash; to our in-house concierge team. Best practices and automations
                                free your time for sales, the one activity that actually generates
                                revenue. Top advisors with strong networks reach{' '}
                                <strong>seven figures in commissions</strong>.
                            </Step>
                        </ol>
                        <div className="mt-10 border-t border-[#0e2727]/15 pt-6">
                            <p className="text-[15px] leading-[1.7] text-[#0e2727]/75 italic">
                                Whichever path you take, the rules that govern travel sales in your
                                state are worth knowing.
                            </p>
                            <a
                                href="#state-regulations"
                                className="lux-caps lux-caps-sm mt-5 inline-block rounded-sm border border-[#0e2727] px-4 py-1.5 text-[#0e2727] transition-colors duration-300 hover:bg-[#0e2727] hover:text-[#E8E0D8]"
                            >
                                Explore travel agent regulations in {state.state}
                            </a>
                        </div>
                    </div>

                    {/* Highlight column — 4 of 12 on md+. Editorial typographic ladder. */}
                    <aside className="md:col-span-4">
                        <Stat number="100%">
                            of new agencies launched in 2024 chose the hosted route
                        </Stat>
                        <hr className="my-7 border-0 border-t border-[#0e2727]/15" />
                        <Stat number="96%">
                            of hosted advisors said they&rsquo;d choose this career again
                        </Stat>
                        <p className="mt-9 text-xs leading-[1.6] text-[#0e2727]/55">
                            Source: <em>2025 Hosted Travel Advisor Report</em>,
                            Host Agency Reviews. n&nbsp;=&nbsp;2,824.
                        </p>
                    </aside>
                </div>
            </div>
        </section>
    );
}

function Step({
    number,
    title,
    children,
}: {
    number: string;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <li className="flex gap-5">
            <span
                className="font-didot tabular-nums leading-none text-[#0e2727]/45 shrink-0"
                style={{ fontSize: 'clamp(1.5rem, 1.8vw, 1.85rem)', marginTop: '0.15em' }}
            >
                {number}
            </span>
            <div>
                <h3
                    className="font-didot text-[#0b2222]"
                    style={{ fontSize: 'clamp(1.15rem, 1.4vw, 1.35rem)', lineHeight: 1.25 }}
                >
                    {title}
                </h3>
                <p className="mt-2 text-base leading-[1.7] text-[#0e2727]/85">{children}</p>
            </div>
        </li>
    );
}

function Stat({ number, children }: { number: string; children: React.ReactNode }) {
    return (
        <div>
            <p
                className="font-didot text-[#0e2727]"
                style={{
                    fontSize: 'clamp(4rem, 7vw, 5.75rem)',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                }}
            >
                {number}
            </p>
            <p
                className="font-didot mt-3 italic text-[#0e2727]/80"
                style={{
                    fontSize: 'clamp(1.05rem, 1.4vw, 1.2rem)',
                    lineHeight: 1.45,
                }}
            >
                {children}
            </p>
        </div>
    );
}
